import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useSwitchCompanyMutation } from '@features/auth/hooks';
import { postLogout } from '@entities/auth/api/auth';
import tryRefreshAccessToken from '@entities/auth/lib/tryRefreshAccessToken';
import useAuthStore from '@entities/auth/model/useAuthStore';
import useUnsavedLeaveStore from '@shared/model/useUnsavedLeaveStore';
import decodeJwt from '@shared/lib/decodeJwt';
import { isMobileApp } from '@shared/utils/fcmToken';
import logoutIcon from '@shared/assets/images/log-out-01.svg';
import ponylinkLogo from '@shared/assets/images/ponylink_dark_logo.png';
import profileIcon from '@shared/assets/images/Profile Avatar.svg';

import styles from './GlobalHeader.module.scss';

// ============================================================
// 스타일 전환 가이드:
// - 현재: 기존 스타일 (드롭다운 + 사용자 정보 + 로그아웃 버튼 분리)
// - ProfileDropdown 스타일로 전환하려면:
//   1. import ProfileDropdown from '@shared/components/molecules/ProfileDropdown';
//   2. rightSection 내부를 <ProfileDropdown ... /> 으로 교체
// ============================================================

// 테스트용 사용자 데이터
const TEST_USER = {
  name: '정링크',
  company: '포니링크',
};

/**
 * GlobalHeader 컴포넌트
 * - 애플리케이션 상단 헤더 바
 * - 로고, 계정전환 드롭다운, 사용자 정보, 로그아웃 버튼 포함
 */
export default function GlobalHeader() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, companyList, companyCode, clearAuth, accessToken, setAccessToken } = useAuthStore();
  const [isExtending, setIsExtending] = useState(false);

  // 실제 사용자 또는 테스트 사용자
  const displayUser = user || TEST_USER;

  // 계정전환 Mutation
  const switchCompanyMutation = useSwitchCompanyMutation({
    onError: ({ friendlyMsg }) => {
      // eslint-disable-next-line no-alert
      alert(friendlyMsg || '계정 전환에 실패했습니다.');
    },
  });

  // 계정전환 가능 여부 (2개 이상 회사가 있을 때만)
  const showAccountSwitch = useMemo(
    function () {
      return companyList && companyList.length > 1;
    },
    [companyList],
  );

  // 액세스 토큰 남은 시간 분:초 (웹 전용, 1초마다 갱신)
  const [remainingTimeLabel, setRemainingTimeLabel] = useState(null);

  useEffect(
    function () {
      if (!accessToken) {
        setRemainingTimeLabel(null);
        return undefined;
      }
      const decoded = decodeJwt(accessToken);
      const exp = decoded?.exp;
      if (typeof exp !== 'number') {
        setRemainingTimeLabel(null);
        return undefined;
      }

      function getRemaining() {
        const remainingMs = exp * 1000 - Date.now();
        if (remainingMs <= 0) return '만료됨';
        const totalSeconds = Math.floor(remainingMs / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
      }

      setRemainingTimeLabel(getRemaining());
      const intervalId = setInterval(function () {
        setRemainingTimeLabel(getRemaining());
      }, 1000);

      return function () {
        clearInterval(intervalId);
      };
    },
    [accessToken],
  );

  // 계정전환 핸들러
  function handleSwitchCompany(event) {
    const targetCompanyCode = Number(event.target.value);
    if (targetCompanyCode && targetCompanyCode !== companyCode) {
      switchCompanyMutation.mutate(targetCompanyCode);
    }
  }

  // 저장 없이 이동 시 확인 (문서 양식 관리 / 결재라인 안내 관리)
  const requestLeave = useUnsavedLeaveStore((state) => state.requestLeave);

  // 로그아웃 핸들러 (실제 로그아웃 로직)
  async function doLogout() {
    // 로그아웃 핸들러

    /**
     * [1] 로그아웃 "이탈 요청" 이벤트 발생
     *
     * - 로그아웃은 페이지 이동과 동일하게
     *   현재 작업 중인 내용(dirty)이 있으면
     *   사용자 확인(confirm)이 선행되어야 함
     *
     * - 실제 로그아웃 로직을 바로 실행하지 않고,
     *   전역 이벤트(app:leave-request)를 통해
     *   현재 화면이 로그아웃을 허용할지 판단하도록 위임
     *
     * - cancelable: true
     *   → 수신 측에서 e.preventDefault() 호출 시
     *     로그아웃 흐름을 중단할 수 있음
     */
    const event = new CustomEvent('app:leave-request', {
      cancelable: true,
      detail: { type: 'logout' },
    });

    /**
     * [2] 전역 이벤트 디스패치
     *
     * - 이 시점에서:
     *   - dirty 상태가 아니면 → 아무 제어도 하지 않음
     *   - dirty 상태이면 → confirm Alert를 띄우고
     *     preventDefault()로 로그아웃을 보류
     */
    window.dispatchEvent(event);

    /**
     * [3] 로그아웃이 차단되었는지 확인
     *
     * - OrganizationManagementPage 등에서
     *   e.preventDefault()가 호출된 경우
     *   defaultPrevented === true
     *
     * - 이 경우:
     *   → 사용자가 confirm에서 선택할 때까지
     *     로그아웃 로직은 실행하지 않음
     */
    if (event.defaultPrevented) {
      return;
    }

    /**
     * [4] 로그아웃이 허용된 경우에만 실제 로그아웃 처리 수행
     *
     * - 서버 로그아웃 API 호출
     * - 실패하더라도 UX 관점에서는
     *   클라이언트 상태를 정리하고 로그인 화면으로 이동
     */
    try {
      await postLogout();
    } catch (error) {
      // 서버 로그아웃 실패(네트워크/토큰 만료 등)여도
      // UX 관점에서는 클라이언트 인증정보를 초기화하고 로그인 화면으로 이동합니다.
    } finally {
      // 리액트 쿼리 캐시 전체 삭제
      // clearAuth()와 함께 실행하여 모든 사용자 데이터를 초기화
      queryClient.clear();
      clearAuth();

      // 모바일 앱(Flutter WebView)인 경우 Flutter로 로그아웃 이벤트 전달
      // WebView의 쿠키, 캐시, 스토리지를 네이티브 레벨에서 삭제
      if (isMobileApp() && window.FlutterChannel?.postMessage) {
        try {
          window.FlutterChannel.postMessage('logout');
        } catch (e) {
          // Flutter 채널 통신 실패 시 무시 (로그아웃 흐름은 계속 진행)
        }
      }

      navigate('/login');
    }
  }

  // 로그아웃 시도 시 저장 없이 이동 확인 후 doLogout 실행
  function handleLogout() {
    requestLeave(doLogout);
  }

  // 토큰 연장 (사용자 클릭 시에만 갱신)
  async function handleExtendToken() {
    if (isExtending) return;
    setIsExtending(true);
    try {
      const newToken = await tryRefreshAccessToken();
      if (newToken) {
        setAccessToken(newToken);
      } else {
        // eslint-disable-next-line no-alert
        alert('세션 연장에 실패했습니다. 다시 로그인해 주세요.');
      }
    } catch {
      // eslint-disable-next-line no-alert
      alert('세션 연장에 실패했습니다. 다시 로그인해 주세요.');
    } finally {
      setIsExtending(false);
    }
  }

  return (
    <header className={styles.header}>
      {/* 왼쪽: 로고 */}
      <div className={styles.logo}>
        <img src={ponylinkLogo} alt="포니링크 로고" className={styles.logoImage} />
      </div>

      {/* 오른쪽: 계정전환 드롭다운 + 사용자 정보 + 로그아웃 */}
      <div className={styles.rightSection}>
        {/* 계정전환 드롭다운 (2개 이상 회사가 있을 때만 표시) */}
        {showAccountSwitch && (
          <div className={styles.accountSwitchWrapper}>
            <select
              className={styles.accountSwitchSelect}
              value={companyCode || ''}
              onChange={handleSwitchCompany}
              disabled={switchCompanyMutation.isPending}
            >
              {companyList.map((company) => (
                <option key={company.companyCode} value={company.companyCode}>
                  {company.company}
                </option>
              ))}
            </select>
            {switchCompanyMutation.isPending && (
              <span className={styles.switchingIndicator}>전환 중...</span>
            )}
          </div>
        )}

        {/* 사용자 정보 */}
        <div className={styles.userInfo}>
          <img src={profileIcon} alt="프로필" className={styles.profileIcon} />
          <span className={styles.welcomeText}>
            {displayUser.company && `${displayUser.company} `}
            <span className={styles.welcomeName}>{displayUser.name}</span>님, 환영합니다!
          </span>
        </div>

        {/* 액세스 토큰 남은 시간(분:초) + 연장 버튼 (웹에서만 표시) */}
        {!isMobileApp() && accessToken && remainingTimeLabel !== null && (
          <div className={styles.tokenExpiryWrapper}>
            <span className={styles.tokenExpiry} title="액세스 토큰 남은 시간">
              남은 시간 {remainingTimeLabel}
            </span>
            <button
              type="button"
              className={styles.extendButton}
              onClick={handleExtendToken}
              disabled={isExtending}
            >
              {isExtending ? '연장 중...' : '연장'}
            </button>
          </div>
        )}

        {/* 로그아웃 버튼 */}
        <button type="button" className={styles.logoutButton} onClick={handleLogout}>
          <img src={logoutIcon} alt="로그아웃" className={styles.logoutIcon} />
          <span>로그아웃</span>
        </button>
      </div>
    </header>
  );
}
